import React, { useState } from 'react';
import { X, Copy, Check, Share2, Link as LinkIcon, Mail, Linkedin, Twitter } from 'lucide-react';
import { withBackend } from '../utils/api';

/**
 * Universal share modal for any content type.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   contentType: 'presentation' | 'dashboard' | 'document' | 'infographic' | 'sheets' | 'analysis'
 *   title: string
 *   userId: string (optional)
 *   htmlContent: string (for dashboard/document/infographic/sheets)
 *   slidesData: array (for presentations — [{html_source, preview_url, ...}])
 *   analysisData: object (for analysis reports, backward compat)
 */
const ShareContentModal = ({ isOpen, onClose, contentType, title, userId, htmlContent, slidesData, analysisData }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expirationDays, setExpirationDays] = useState(30);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const createShareLink = async () => {
    setIsCreating(true);
    setError(null);

    try {
      const response = await fetch(withBackend('/api/share-content'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content_type: contentType,
          title: title || 'Untitled',
          expiration_days: expirationDays,
          user_id: userId,
          html_content: htmlContent || null,
          slides_data: slidesData || null,
          analysis_data: analysisData || null,
        }),
      });

      if (!response.ok) throw new Error('Failed to create share link');

      const data = await response.json();
      if (data.success) {
        setShareLink(data.url);
      } else {
        throw new Error(data.error || 'Failed to create share link');
      }
    } catch (err) {
      console.error('Error creating share link:', err);
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = async () => {
    if (shareLink) {
      try {
        await navigator.clipboard.writeText(shareLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const contentLabel = {
    presentation: 'Presentation',
    dashboard: 'Dashboard',
    document: 'Document',
    infographic: 'Infographic',
    sheets: 'Sheet',
    analysis: 'Analysis Report',
  }[contentType] || 'Content';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-foreground">Share {contentLabel}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg transition-colors">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {!shareLink ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Create a public link to share this {contentLabel.toLowerCase()}. Anyone with the link can view it.
            </p>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Link expires after:</label>
              <div className="grid grid-cols-3 gap-2">
                {[{ label: '7 days', value: 7 }, { label: '30 days', value: 30 }, { label: '90 days', value: 90 }].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setExpirationDays(opt.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      expirationDays === opt.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <button
              onClick={createShareLink}
              disabled={isCreating}
              className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isCreating ? 'Creating link...' : 'Create Share Link'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Your link is ready! Expires in {expirationDays} days.
            </p>

            {/* Link display */}
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg border border-border">
              <LinkIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <input type="text" value={shareLink} readOnly className="flex-1 bg-transparent text-sm text-foreground outline-none" />
              <button onClick={copyToClipboard} className="p-1.5 hover:bg-background rounded transition-colors" title="Copy link">
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
              </button>
            </div>

            {/* Social share */}
            <div className="flex gap-2">
              <button
                onClick={() => window.open(`mailto:?subject=${encodeURIComponent(`Check out: ${title}`)}&body=${encodeURIComponent(shareLink)}`, '_blank')}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-muted hover:bg-muted/80 text-muted-foreground rounded-lg text-sm transition-colors"
              >
                <Mail className="w-4 h-4" /> Email
              </button>
              <button
                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`, '_blank')}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-lg text-sm transition-colors"
              >
                <Linkedin className="w-4 h-4" /> LinkedIn
              </button>
              <button
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareLink)}`, '_blank')}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white rounded-lg text-sm transition-colors"
              >
                <Twitter className="w-4 h-4" /> Twitter
              </button>
            </div>

            <button onClick={onClose} className="w-full px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg font-medium transition-colors">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareContentModal;
